import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Calendar and availability
export const getCalendar = async (year, month) => {
  const response = await api.get(`/calendar/${year}/${month}`);
  return response.data;
};

// Booking services
export const calculatePrice = async (checkIn, checkOut, guests = 1) => {
  const response = await api.post('/bookings/calculate-price', {
    check_in: checkIn,
    check_out: checkOut,
    guests
  });
  return response.data;
};

export const checkAvailability = async (checkIn, checkOut) => {
  const response = await api.post('/bookings/check-availability', {
    check_in: checkIn,
    check_out: checkOut
  });
  return response.data;
};

export const createBooking = async (checkIn, checkOut, guests = 1, specialRequests = null) => {
  const response = await api.post('/bookings', {
    check_in: checkIn,
    check_out: checkOut,
    guests,
    special_requests: specialRequests
  });
  return response.data;
};

export const getUserBookings = async () => {
  const response = await api.get('/bookings');
  return response.data;
};

export const getBooking = async (bookingId) => {
  const response = await api.get(`/bookings/${bookingId}`);
  return response.data;
};

export const cancelBooking = async (bookingId) => {
  const response = await api.delete(`/bookings/${bookingId}`);
  return response.data;
};

// Gallery
export const getGallery = async (category = null) => {
  const url = category ? `/gallery?category=${category}` : '/gallery';
  const response = await api.get(url);
  return response.data;
};

// Activities
export const getActivities = async () => {
  const response = await api.get('/activities');
  return response.data;
};

export const getActivity = async (activityId) => {
  const response = await api.get(`/activities/${activityId}`);
  return response.data;
};

// Amenities
export const getAmenities = async () => {
  const response = await api.get('/amenities');
  return response.data;
};

// Public Settings
export const getPublicSettings = async () => {
  const response = await api.get('/settings/public');
  return response.data;
};

// ============ ADMIN APIs ============

// Admin: Users
export const adminGetUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

export const adminGetUser = async (userId) => {
  const response = await api.get(`/admin/users/${userId}`);
  return response.data;
};

export const adminUpdateUser = async (userId, data) => {
  const response = await api.put(`/admin/users/${userId}`, data);
  return response.data;
};

export const adminDeleteUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

// Admin: Bookings
export const adminGetBookings = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.from_date) params.append('from_date', filters.from_date);
  if (filters.to_date) params.append('to_date', filters.to_date);
  
  const response = await api.get(`/admin/bookings?${params.toString()}`);
  return response.data;
};

export const adminCreateBooking = async (data) => {
  const response = await api.post('/admin/bookings', data);
  return response.data;
};

export const adminUpdateBooking = async (bookingId, data) => {
  const response = await api.put(`/admin/bookings/${bookingId}`, data);
  return response.data;
};

export const adminDeleteBooking = async (bookingId) => {
  const response = await api.delete(`/admin/bookings/${bookingId}`);
  return response.data;
};

// Admin: Gallery
export const adminGetGallery = async () => {
  const response = await api.get('/admin/gallery');
  return response.data;
};

export const adminAddGalleryImage = async (data) => {
  const response = await api.post('/admin/gallery', data);
  return response.data;
};

export const adminUpdateGalleryImage = async (imageId, data) => {
  const response = await api.put(`/admin/gallery/${imageId}`, data);
  return response.data;
};

export const adminReorderGallery = async (imageIds) => {
  const response = await api.post('/admin/gallery/reorder', { image_ids: imageIds });
  return response.data;
};

export const adminDeleteGalleryImage = async (imageId) => {
  const response = await api.delete(`/admin/gallery/${imageId}`);
  return response.data;
};

// Admin: Activities
export const adminGetActivities = async () => {
  const response = await api.get('/admin/activities');
  return response.data;
};

export const adminCreateActivity = async (data) => {
  const response = await api.post('/admin/activities', data);
  return response.data;
};

export const adminUpdateActivity = async (activityId, data) => {
  const response = await api.put(`/admin/activities/${activityId}`, data);
  return response.data;
};

export const adminDeleteActivity = async (activityId) => {
  const response = await api.delete(`/admin/activities/${activityId}`);
  return response.data;
};

// Admin: Amenities
export const adminGetAmenities = async () => {
  const response = await api.get('/admin/amenities');
  return response.data;
};

export const adminCreateAmenity = async (data) => {
  const response = await api.post('/admin/amenities', data);
  return response.data;
};

export const adminUpdateAmenity = async (amenityId, data) => {
  const response = await api.put(`/admin/amenities/${amenityId}`, data);
  return response.data;
};

export const adminDeleteAmenity = async (amenityId) => {
  const response = await api.delete(`/admin/amenities/${amenityId}`);
  return response.data;
};

// Admin: Settings
export const adminGetSettings = async () => {
  const response = await api.get('/admin/settings');
  return response.data;
};

export const adminUpdateSettings = async (data) => {
  const response = await api.put('/admin/settings', data);
  return response.data;
};

// Admin: Stats
export const adminGetStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

export default api;
